/**
 * Methods race — 13 turning-point methods × 50 stocks + 10 indexes/ETFs, last 6 months.
 * Ranked overall, stocks vs indexes, per sector, per index. Read-only view of the stored
 * result produced by app.backtesting.turning_points_race.
 */

import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Table, TableBody, TableCell,
  TableHead, TableRow, Select, MenuItem, FormControl, InputLabel, Tooltip,
} from '@mui/material';
import { EmojiEvents, TrendingUp, TrendingDown } from '@mui/icons-material';
import apiService from '../../services/api';

const pct = (v?: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`);

function RankTable({ pool, title, limit = 26 }: { pool: any; title: string; limit?: number }) {
  if (!pool) return null;
  const rows = pool.ranking.slice(0, limit);
  return (
    <Card sx={{ mb: 2 }}><CardContent sx={{ p: 0 }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
        <Chip size="small" label={`${pool.symbols.length} symbols`} sx={{ height: 18, fontSize: '0.62rem' }} />
        <Typography variant="caption" color="text.secondary">
          base rate: turn up {pct(pool.base_rate_pct.up)} · turn down {pct(pool.base_rate_pct.down)} ({pool.base_counts.n} bars)
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { py: 0.5, whiteSpace: 'nowrap' } }}>
          <TableHead><TableRow>
            {['#', 'Method', 'School', 'Direction', 'Hit rate', 'Base', 'Lift', 'n', '95% CI', 'Verdict'].map(h => (
              <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>{h}</TableCell>
            ))}
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r: any) => {
              const color = r.proven ? '#10B981' : r.reliable && r.lift != null && r.lift < 0 ? '#EF4444' : undefined;
              return (
                <TableRow key={`${r.method}-${r.direction}`} hover sx={{ opacity: r.reliable ? 1 : 0.55 }}>
                  <TableCell sx={{ fontSize: '0.72rem' }}>{r.rank}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell><Chip size="small" label={r.family} sx={{ height: 17, fontSize: '0.6rem' }} /></TableCell>
                  <TableCell>
                    <Chip size="small" icon={r.direction === 'up' ? <TrendingUp sx={{ fontSize: 12 }} /> : <TrendingDown sx={{ fontSize: 12 }} />}
                      label={r.direction === 'up' ? 'bottoms' : 'tops'}
                      sx={{ height: 18, fontSize: '0.62rem', color: r.direction === 'up' ? '#10B981' : '#EF4444', bgcolor: r.direction === 'up' ? '#10B98122' : '#EF444422' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 'bold', color }}>{pct(r.hit_rate_pct)}</TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', opacity: 0.7 }}>{pct(r.base_rate_pct)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color }}>{r.lift == null ? '—' : `${r.lift > 0 ? '+' : ''}${r.lift}`}</TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>{r.firings}</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', opacity: 0.7 }}>{r.ci95[0] == null ? '—' : `${r.ci95[0]}–${r.ci95[1]}`}</TableCell>
                  <TableCell>
                    {r.proven ? <Chip size="small" label="proven" color="success" sx={{ height: 18, fontSize: '0.62rem' }} />
                      : !r.reliable ? <Tooltip title="fewer than 15 firings in the window"><Chip size="small" label="thin" sx={{ height: 18, fontSize: '0.62rem' }} /></Tooltip>
                      : r.lift != null && r.lift < 0 ? <Chip size="small" label="below base" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                      : <Chip size="small" label="≈ base" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </CardContent></Card>
  );
}

function WinnerCell({ w }: { w: any }) {
  if (!w) return <Typography variant="caption" color="text.disabled">no method beats base</Typography>;
  const proven = w.status === 'proven';
  return (
    <Tooltip title={`${w.family} · n=${w.firings} · 95% CI ${w.ci95?.[0]}–${w.ci95?.[1]}%${w.runner_up ? ` · runner-up: ${w.runner_up.name} (+${w.runner_up.lift}, n=${w.runner_up.firings})` : ''}`}>
      <Box>
        <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{w.name}</Typography>
        <Typography variant="caption" sx={{ color: proven ? '#10B981' : 'text.secondary' }}>
          {pct(w.hit_rate_pct)} vs {pct(w.base_rate_pct)} · +{w.lift} · n={w.firings}
        </Typography>
        <Box sx={{ mt: 0.3 }}>
          {proven ? <Chip size="small" label="proven" color="success" sx={{ height: 16, fontSize: '0.58rem' }} />
            : <Chip size="small" label="leader, not proven" variant="outlined" sx={{ height: 16, fontSize: '0.58rem' }} />}
        </Box>
      </Box>
    </Tooltip>
  );
}

export function WinnersGrid({ winners }: { winners: any }) {
  const rows: [string, any][] = [
    ['All 60 symbols', winners.all], ['50 stocks', winners.stocks], ['10 indexes & ETFs', winners.indexes],
    ...Object.entries(winners.by_sector).map(([k, v]) => [`Sector: ${k}`, v] as [string, any]),
  ];
  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: '#F59E0B55' }}><CardContent sx={{ p: 0 }}>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="bold">Who wins where</Typography>
        <Typography variant="caption" color="text.secondary">
          The best method per scope and direction. <b>Proven</b> = its 95% CI clears the base rate; <b>leader</b> = highest lift with ≥15 firings but the sample cannot yet separate it from chance. Hover for the runner-up.
        </Typography>
      </Box>
      <Table size="small" sx={{ '& td, & th': { py: 0.8, verticalAlign: 'top' } }}>
        <TableHead><TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Scope</TableCell>
          <TableCell sx={{ fontWeight: 'bold', color: '#10B981' }}>Best for BOTTOMS (turn up)</TableCell>
          <TableCell sx={{ fontWeight: 'bold', color: '#EF4444' }}>Best for TOPS (turn down)</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map(([label, w]) => (
            <TableRow key={label} hover>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{label}</TableCell>
              <TableCell><WinnerCell w={w.up} /></TableCell>
              <TableCell><WinnerCell w={w.down} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

export default function TurningPointsRace() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState('all');
  const [evalBars, setEvalBars] = useState(126);
  useEffect(() => {
    setData(null);
    apiService.getTurningPointsRace(evalBars).then(setData).catch(e => setError(e?.response?.data?.detail || e.message));
  }, [evalBars]);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const scopes: Record<string, { label: string; pool: any }> = {
    all: { label: 'All 60 symbols', pool: data.all },
    stocks: { label: '50 stocks', pool: data.stocks },
    indexes: { label: '10 indexes & ETFs', pool: data.indexes },
    ...Object.fromEntries(Object.entries(data.by_sector).map(([k, v]) => [`sector:${k}`, { label: `Sector: ${k}`, pool: v }])),
    ...Object.fromEntries(Object.entries(data.by_index).map(([k, v]) => [`index:${k}`, { label: `Index: ${k}`, pool: v }])),
  };
  const provenAll = data.all.ranking.filter((r: any) => r.proven);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <EmojiEvents sx={{ color: '#F59E0B' }} />
        <Typography variant="h6" fontWeight="bold">Methods race — last {evalBars === 126 ? '6 months' : '12 months'}</Typography>
        <Typography variant="caption" color="text.secondary">
          generated {data.generated_at?.slice(0, 16).replace('T', ' ')} · {data.eval_bars} trading days · {Object.keys(data.errors || {}).length} symbols failed
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Window</InputLabel>
          <Select value={evalBars} label="Window" onChange={e => setEvalBars(Number(e.target.value))}>
            <MenuItem value={126}>Last 6 months</MenuItem>
            <MenuItem value={252}>Last 12 months</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Scope</InputLabel>
          <Select value={scope} label="Scope" onChange={e => setScope(e.target.value)}>
            {Object.entries(scopes).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 980, lineHeight: 1.6 }}>
        {data.definition}. Hits and firings are pooled across the symbols in scope — one symbol's six months hold only a
        handful of firings — and ranked by lift over the pooled base rate. <b>Proven</b> = the 95% CI lies above the base
        rate with at least {data.min_firings} firings. A method can be proven for one sector and useless for another; pick the scope.
      </Typography>

      {provenAll.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Across all 60 symbols, proven: {provenAll.map((r: any) => `${r.name} (${r.direction === 'up' ? 'bottoms' : 'tops'}, ${pct(r.hit_rate_pct)} vs ${pct(r.base_rate_pct)}, n=${r.firings})`).join(' · ')}
        </Alert>
      )}
      {provenAll.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>Across all 60 symbols, no method's hit rate clears the base rate at 95% confidence over the last six months.</Alert>
      )}

      {data.winners && <WinnersGrid winners={data.winners} />}
      <RankTable pool={scopes[scope].pool} title={scopes[scope].label} />
    </Box>
  );
}


/** Compact "who wins where" for other pages: loads the stored race for one window. */
export function WinnersSummary() {
  const [evalBars, setEvalBars] = useState(126);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    apiService.getTurningPointsRace(evalBars).then(setData).catch(e => setError(e?.response?.data?.detail || e.message));
  }, [evalBars]);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
          From the methods race: 13 methods × 50 stocks + 10 indexes, pooled per scope, ranked by lift over base rate.
          Use it to weight this ticker's signals — a method that is proven for its sector deserves more attention than one that is not.
          {data?.generated_at && <> Generated {data.generated_at.slice(0, 10)}.</>}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Window</InputLabel>
          <Select value={evalBars} label="Window" onChange={e => setEvalBars(Number(e.target.value))}>
            <MenuItem value={126}>Last 6 months</MenuItem>
            <MenuItem value={252}>Last 12 months</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {error ? <Alert severity="info">{error}</Alert>
        : !data ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
        : data.winners ? <WinnersGrid winners={data.winners} /> : null}
    </Box>
  );
}
